import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Star, ShieldCheck, Sparkles, ArrowRight, PlayCircle, BadgeCheck } from "lucide-react";
import { HERO } from "@/constants/testIds";
import BookCallButton from "@/components/site/BookCallButton";

/* ----------------------------------------------------------------------------
 * Kinetic on-load headline — each word is wrapped in a mask and rises from
 * below with a stagger. This is the signature moment of the page.
 * -------------------------------------------------------------------------- */
function KineticHeadline() {
  // Split into lines/words. Each word has role: normal | gold | emerald.
  const lines = [
    [
      { t: "Remove", role: "normal" },
      { t: "damaging", role: "normal" },
      { t: "Google", role: "normal" },
      { t: "reviews.", role: "normal" },
    ],
    [
      { t: "Pay", role: "gold" },
      { t: "only", role: "gold" },
      { t: "when", role: "normal" },
      { t: "they're", role: "normal" },
      { t: "gone.", role: "emerald" },
    ],
  ];

  return (
    <h1
      className="font-display font-bold tracking-[-0.03em] text-5xl sm:text-6xl lg:text-[5.5rem] xl:text-[6rem] leading-[0.95] mt-6"
      aria-label="Remove damaging Google reviews. Pay only when they're gone."
    >
      {lines.map((words, li) => (
        <div key={li} className="overflow-hidden pb-[0.05em] flex flex-wrap gap-x-[0.28em]">
          {words.map((w, wi) => {
            const delay = 0.15 + li * 0.15 + wi * 0.08;
            const color =
              w.role === "gold"
                ? "text-[#D4AF37]"
                : w.role === "emerald"
                ? "text-[#10B981]"
                : "text-white";
            return (
              <motion.span
                key={wi}
                initial={{ y: "115%", opacity: 0, rotate: 6 }}
                animate={{ y: "0%", opacity: 1, rotate: 0 }}
                transition={{ duration: 0.85, delay, ease: [0.22, 1, 0.36, 1] }}
                className={`inline-block will-change-transform ${color}`}
              >
                {w.t}
              </motion.span>
            );
          })}
        </div>
      ))}
    </h1>
  );
}

/* ----------------------------------------------------------------------------
 * Review card animation state — the cycling 1-star → removed sequence.
 * -------------------------------------------------------------------------- */
function ReviewCard() {
  const [stage, setStage] = useState(0);
  const [rating, setRating] = useState(3.2);
  const [litStars, setLitStars] = useState(0);
  const [burst, setBurst] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 2200);
    const t2 = setTimeout(() => {
      setStage(2);
      setBurst(true);
      setTimeout(() => setBurst(false), 900);
    }, 3400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  useEffect(() => {
    if (stage !== 2) return;
    let raf;
    const start = performance.now();
    const from = 3.2, to = 4.8, dur = 1400;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setRating(from + (to - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [stage]);

  useEffect(() => {
    if (stage !== 2) return;
    const ids = [];
    [0, 1, 2, 3, 4].forEach((i) => {
      ids.push(setTimeout(() => setLitStars(i + 1), 200 + i * 220));
    });
    return () => ids.forEach(clearTimeout);
  }, [stage]);

  useEffect(() => {
    if (stage !== 2) return;
    const loop = setTimeout(() => {
      setStage(0);
      setRating(3.2);
      setLitStars(0);
    }, 5200);
    return () => clearTimeout(loop);
  }, [stage]);

  return (
    <div data-testid={HERO.reviewCard} className="relative w-[340px] sm:w-[380px] max-w-full">
      <div className="absolute -inset-8 -z-10">
        <div className="absolute top-6 -right-6 w-56 h-56 rounded-full bg-[#10B981]/25 blur-3xl" />
        <div className="absolute -bottom-6 -left-6 w-48 h-48 rounded-full bg-[#D4AF37]/20 blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30, rotate: -2 }}
        animate={{ opacity: 1, y: 0, rotate: -1.5 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="relative rounded-2xl border border-white/10 bg-[#0d1424]/90 backdrop-blur-xl p-6 shadow-2xl shadow-black/50"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8a6f1e] grid place-items-center font-display font-bold text-[#0A0F1C]">
            R
          </div>
          <div className="flex-1">
            <div className="text-white font-semibold text-sm">Riverside Bistro</div>
            <div className="text-white/50 text-xs">Google Business Profile</div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-display font-bold tabular-nums text-white">
              {rating.toFixed(1)}
            </div>
            <div className="flex gap-0.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <motion.span
                  key={i}
                  animate={i < litStars ? { scale: [1, 1.35, 1] } : { scale: 1 }}
                  transition={{ duration: 0.35 }}
                >
                  <Star
                    className={`w-3 h-3 transition-colors duration-300 ${
                      i < litStars ? "fill-[#10B981] text-[#10B981]" : "fill-white/10 text-white/20"
                    }`}
                  />
                </motion.span>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-5 border-t border-white/5 pt-4">
          <AnimatePresence mode="wait">
            {stage < 2 && (
              <motion.div
                key="badreview"
                initial={{ opacity: 1 }}
                animate={{
                  opacity: stage === 1 ? 0 : 1,
                  filter: stage === 1 ? "blur(6px)" : "blur(0px)",
                }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.9 }}
                className="space-y-2"
              >
                <div className="flex items-center gap-2">
                  <div className="flex">
                    <Star className="w-3.5 h-3.5 fill-red-400 text-red-400" />
                    {[0, 1, 2, 3].map((i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-white/10 text-white/20" />
                    ))}
                  </div>
                  <span className="text-white/40 text-xs">1 star · anonymous</span>
                </div>
                <div className="text-white/70 text-sm leading-relaxed">
                  "Never ate here but heard the owner is rude. Would not recommend to anyone in this town."
                </div>
                <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-red-300/80 mt-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                  Policy violation: never visited
                </div>
              </motion.div>
            )}
            {stage === 2 && (
              <motion.div
                key="removed"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="space-y-3"
              >
                <div className="flex items-center gap-2 text-[#10B981] text-sm font-semibold">
                  <BadgeCheck className="w-4 h-4" />
                  Review removed by Google
                </div>
                <div className="text-white/60 text-sm leading-relaxed">
                  Flagged for policy violation. Rating recovered — new customers now see the real you.
                </div>
                <div className="flex items-center gap-2 text-xs text-[#D4AF37]">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span className="font-medium">+1.6 stars in 14 days</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      <AnimatePresence>
        {stage === 2 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.7, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            className="absolute -top-4 -right-4 rounded-full bg-[#10B981] text-white text-[11px] font-semibold px-3 py-1.5 shadow-lg shadow-emerald-500/30 flex items-center gap-1.5"
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            Removed
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {burst && (
          <div className="absolute inset-0 pointer-events-none">
            {Array.from({ length: 12 }).map((_, i) => {
              const angle = (i / 12) * Math.PI * 2;
              const dx = Math.cos(angle) * 90;
              const dy = Math.sin(angle) * 90;
              return (
                <motion.span
                  key={i}
                  initial={{ opacity: 1, x: 0, y: 0, scale: 0.4 }}
                  animate={{ opacity: 0, x: dx, y: dy, scale: 1 }}
                  transition={{ duration: 0.9, ease: "easeOut" }}
                  className="absolute top-1/2 left-1/2 w-1.5 h-1.5 rounded-full bg-[#D4AF37] shadow-[0_0_8px_#D4AF37]"
                />
              );
            })}
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * Mouse-follow 3D parallax wrapper — the signature depth moment.
 * -------------------------------------------------------------------------- */
function TiltCard({ children }) {
  const ref = useRef(null);
  const mvX = useMotionValue(0);
  const mvY = useMotionValue(0);
  const springCfg = { stiffness: 120, damping: 15, mass: 0.6 };
  const rotX = useSpring(useTransform(mvY, [-0.5, 0.5], [10, -10]), springCfg);
  const rotY = useSpring(useTransform(mvX, [-0.5, 0.5], [-14, 14]), springCfg);
  const trZ = useSpring(useTransform(mvX, [-0.5, 0, 0.5], [8, 0, 8]), springCfg);
  const shineX = useTransform(mvX, [-0.5, 0.5], ["20%", "80%"]);
  const shineY = useTransform(mvY, [-0.5, 0.5], ["25%", "75%"]);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    mvX.set(x);
    mvY.set(y);
  };
  const onLeave = () => {
    mvX.set(0);
    mvY.set(0);
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ perspective: 1200 }}
      className="relative"
    >
      <motion.div
        style={{
          rotateX: rotX,
          rotateY: rotY,
          translateZ: trZ,
          transformStyle: "preserve-3d",
        }}
        className="relative will-change-transform"
      >
        {children}
        {/* subtle specular shine that follows the cursor */}
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-2xl mix-blend-overlay"
          style={{
            background: `radial-gradient(400px circle at ${shineX} ${shineY}, rgba(255,255,255,0.18), transparent 45%)`,
          }}
        />
      </motion.div>
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * Floating particles behind the hero
 * -------------------------------------------------------------------------- */
function Particles() {
  const particles = Array.from({ length: 22 });
  return (
    <div aria-hidden className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
      {particles.map((_, i) => {
        const left = (i * 47) % 100;
        const top = (i * 83) % 100;
        const size = 2 + ((i * 7) % 4);
        const dur = 6 + ((i * 3) % 6);
        const delay = (i % 5) * 0.6;
        const isGold = i % 4 === 0;
        return (
          <motion.span
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: [0, 0.6, 0], y: [-20, -140] }}
            transition={{ duration: dur, delay, repeat: Infinity, ease: "easeInOut" }}
            className={`absolute rounded-full ${isGold ? "bg-[#D4AF37]" : "bg-[#10B981]"}`}
            style={{
              left: `${left}%`,
              top: `${top}%`,
              width: size,
              height: size,
              filter: "blur(0.5px)",
              boxShadow: isGold ? "0 0 12px #D4AF37" : "0 0 12px #10B981",
            }}
          />
        );
      })}
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * HERO
 * -------------------------------------------------------------------------- */
export default function Hero() {
  return (
    <section id="top" className="relative overflow-hidden pt-36 pb-24 lg:pt-44 lg:pb-32 min-h-[92vh] flex flex-col">
      {/* Aurora conic gradient orbs */}
      <div aria-hidden className="absolute inset-0 -z-20 overflow-hidden">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, rotate: 360 }}
          transition={{ opacity: { duration: 1 }, rotate: { duration: 40, repeat: Infinity, ease: "linear" } }}
          className="absolute -top-40 -right-40 w-[720px] h-[720px] rounded-full opacity-40"
          style={{
            background:
              "conic-gradient(from 90deg, rgba(16,185,129,0.35), rgba(212,175,55,0.25), rgba(16,185,129,0.0), rgba(16,185,129,0.35))",
            filter: "blur(80px)",
          }}
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, rotate: -360 }}
          transition={{ opacity: { duration: 1 }, rotate: { duration: 55, repeat: Infinity, ease: "linear" } }}
          className="absolute -bottom-56 -left-40 w-[620px] h-[620px] rounded-full opacity-30"
          style={{
            background:
              "conic-gradient(from 0deg, rgba(212,175,55,0.25), rgba(16,185,129,0.15), rgba(212,175,55,0.0), rgba(212,175,55,0.25))",
            filter: "blur(70px)",
          }}
        />
      </div>

      {/* Grid background */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(ellipse at 50% 20%, black 40%, transparent 75%)",
        }}
      />

      <Particles />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center flex-1">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur px-3 py-1.5 text-xs text-white/70"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#10B981] opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#10B981]" />
            </span>
            Ethical Google review removal · No fake reviews. Ever.
          </motion.div>

          <KineticHeadline />

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-8 max-w-xl text-white/60 text-base sm:text-lg leading-relaxed"
          >
            We identify reviews that violate Google's own content policies — fake, off-topic,
            conflicts of interest, harassment — and get them removed through Google's legitimate
            escalation channels. Start with a free audit. You pay $195 only after a review is
            successfully removed.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-10 flex flex-col sm:flex-row gap-3"
          >
            <a
              href="#audit"
              data-testid={HERO.ctaPrimary}
              className="group relative inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-[#10B981] hover:bg-[#0ea774] text-white font-semibold text-sm transition-colors shadow-lg shadow-emerald-500/30 overflow-hidden"
            >
              <span
                aria-hidden
                className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              />
              <span className="relative">Get my free audit</span>
              <ArrowRight className="relative w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </a>
            <a
              href="#how"
              data-testid={HERO.ctaSecondary}
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full border border-white/15 hover:border-white/30 bg-white/[0.02] hover:bg-white/[0.05] text-white text-sm font-semibold transition-colors"
            >
              <PlayCircle className="w-4 h-4" />
              See how it works
            </a>
            <BookCallButton
              variant="secondary"
              label="Book a free call"
              testId="hero-book-call"
              className="h-12 px-6"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.25 }}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-white/50"
          >
            <div className="inline-flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#10B981]" />
              No removal, no charge — guaranteed
            </div>
            <div className="hidden sm:block h-3 w-px bg-white/10" />
            <div className="inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#D4AF37]" />
              Google-compliant · Zero black-hat tactics
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-5 flex justify-center lg:justify-end">
          <div className="animate-float">
            <TiltCard>
              <ReviewCard />
            </TiltCard>
          </div>
        </div>
      </div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-white/40"
      >
        <span>Scroll</span>
        <motion.span
          animate={{ y: [0, 6, 0], opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-8 bg-white/40"
        />
      </motion.div>
    </section>
  );
}
